import React from "react";

/**
 * AssessmentModalShell — the one visual shell the two Assessment modals
 * share (main /assessments page + student-profile Assessments tab).
 *
 * Both pages use different backend endpoints and different form state,
 * so we don't share those. We DO share the visual chrome (overlay, navy
 * gradient header, student badge, footer with Cancel + primary buttons)
 * so both flows look identical.
 *
 * Callers pass their form fields as children.
 */

export interface AssessmentModalShellProps {
  open: boolean;
  onClose: () => void;
  editing?: boolean;
  /** Header top eyebrow, e.g. "New Assessment" or "Edit Assessment". */
  eyebrow?: string;
  /** Big header title, e.g. "📝 Create Assessment Record". */
  title?: string;
  /** Optional student-badge card shown at the top of the body when the
   *  student is pre-filled (student profile tab). */
  studentBadge?: {
    name: string;
    initial?: string;
  };
  /** Rendered inside the scrollable body. */
  children: React.ReactNode;
  /** Save button label (defaults change based on `editing`). */
  saveLabel?: string;
  /** Called when the primary button is clicked. */
  onSave: () => void;
  /** Disable the primary button (validation / loading). */
  saveDisabled?: boolean;
  /** Show a spinner label on the primary button. */
  saving?: boolean;
}

const AssessmentModalShell: React.FC<AssessmentModalShellProps> = ({
  open, onClose, editing, eyebrow, title,
  studentBadge, children, saveLabel, onSave,
  saveDisabled, saving,
}) => {
  if (!open) return null;

  const resolvedEyebrow = eyebrow ?? (editing ? "Edit Assessment" : "New Assessment");
  const resolvedTitle = title ?? `📝 ${editing ? "Update" : "Create"} Assessment Record`;
  const resolvedSaveLabel = saveLabel
    ?? (saving ? "⏳ Saving…" : editing ? "💾 Update Assessment" : "✅ Create Assessment");

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        zIndex: 1400, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20, width: 620,
          maxWidth: "100%", maxHeight: "92vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Navy gradient header */}
        <div style={{
          background: "linear-gradient(135deg,#1b3a6b,#2a5298)",
          padding: "20px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{
              color: "rgba(255,255,255,0.7)", fontSize: 11,
              textTransform: "uppercase", letterSpacing: "0.8px",
            }}>{resolvedEyebrow}</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, marginTop: 2 }}>
              {resolvedTitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              color: "#fff", width: 32, height: 32, borderRadius: 8,
              cursor: "pointer", fontSize: 18,
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
          {studentBadge && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 14px", background: "#f8fafc",
              borderRadius: 10, marginBottom: 16,
              border: "1px solid #e2e8f0",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "#1b3a6b", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 14, flexShrink: 0,
              }}>
                {(studentBadge.initial ?? studentBadge.name.charAt(0)).toUpperCase()}
              </div>
              <div>
                <div style={{
                  fontSize: 11, color: "#94a3b8", fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>Student</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1b3a6b" }}>
                  {studentBadge.name}
                </div>
              </div>
            </div>
          )}
          {children}
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px", borderTop: "1px solid #e2e8f0",
          display: "flex", gap: 10, background: "#f8fafc",
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, background: "#fff", border: "1.5px solid #e2e8f0",
              borderRadius: 10, padding: 12, fontWeight: 600,
              fontSize: 14, cursor: "pointer", color: "#64748b",
            }}
          >Cancel</button>
          <button
            onClick={onSave}
            disabled={saveDisabled}
            style={{
              flex: 2,
              background: saveDisabled
                ? "#e2e8f0"
                : "linear-gradient(135deg,#1b3a6b,#2a5298)",
              color: saveDisabled ? "#94a3b8" : "#fff",
              border: "none", borderRadius: 10, padding: 12,
              fontWeight: 800, fontSize: 14,
              cursor: saveDisabled ? "not-allowed" : "pointer",
              boxShadow: saveDisabled ? "none" : "0 4px 14px rgba(27,58,107,0.4)",
            }}
          >{resolvedSaveLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentModalShell;
